import React, { useCallback, useContext, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ProgressBar } from '@fluentui/react-components';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { useResponsiveSize } from '@adscore/with-responsive-size';

import { getFeatureProgressItemStyles } from './feature-progress-item-styles';

const MAX_PROGRESS = 150;

export function FeatureProgressItem({
  title, description, isActive, scrollToNextSection, setActive,
}) {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getFeatureProgressItemStyles);
  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);
  const [timer, setTimer] = useState(0);

  const timerRef = useRef(null);

  const stopTimer = useCallback(() => {
    if (timerRef && timerRef.current) {
      clearInterval(timerRef.current);
    }
    setTimer(0);
  }, []);

  const startTimer = useCallback(() => {
    if (timerRef && timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      setTimer(value => (value < MAX_PROGRESS ? value + 1 : MAX_PROGRESS));
    }, 100);
  }, []);

  useEffect(() => {
    if (isActive) {
      startTimer();
    } else {
      stopTimer();
    }
  }, [isActive, startTimer, stopTimer]);

  useEffect(() => {
    if (timer === MAX_PROGRESS) {
      scrollToNextSection();
      stopTimer();
    }
  }, [timer, scrollToNextSection, stopTimer]);

  useEffect(() => () => {
    stopTimer();
  }, [stopTimer]);

  return (
    <div className={classNames('feature-progress-item', classes.itemContainer)}>
      <button className={classNames('feature-progress-item-title', classes.itemTitle, classes[`itemTitle-${displayMode}`])} onClick={setActive}>
        {i18n.getString(title)}
      </button>
      {isActive &&
        <div className={classNames('feature-progress-item-description', classes.itemDescription)}>
          {i18n.getString(description)}
        </div>
      }
      <ProgressBar
        max={MAX_PROGRESS}
        value={timer}
        className={classNames('item-progress-bar', classes.itemProgressBar)}
        aria-label={i18n.getString(_TL_('feature progress bar'))}
      />
    </div>
  );
}

FeatureProgressItem.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  setActive: PropTypes.func.isRequired,
  scrollToNextSection: PropTypes.func.isRequired,
};
